/**
 * GeneticTrainer - 遺伝的アルゴリズムトレーナー
 *
 * AIGenomeパラメータを進化させ、より強いAIを育成する。
 * Simulatorを使用して適応度を評価。
 */

import type { CardWithId } from '../../../types/card';
import type { PlayerNumber } from '../../../types/player';
import {
  type AIGenome,
  DEFAULT_GENOME,
  EVOLVED_GENOME,
  createRandomGenome,
  cloneGenome,
  normalizeGenome,
} from './genome';
import {
  crossover,
  mutate,
  tournamentSelection,
  eliteSelection,
  type Individual,
} from './operators';
import { createGeneticStrategy } from '../strategies/genetic';
import { createGeneticSimulationStrategy } from '../strategies/simulation';
import { hardStrategy } from '../strategies/hard';
import type { AIStrategy } from '../strategies/base';

// =============================================================================
// 設定
// =============================================================================

/** 対戦相手タイプ */
export type OpponentType = 'self' | 'evolved' | 'hard';

/** GA設定 */
export interface GAConfig {
  /** 個体数（デフォルト: 50） */
  populationSize: number;
  /** 世代数（デフォルト: 100） */
  generations: number;
  /** エリート保存数（デフォルト: 5） */
  eliteCount: number;
  /** 突然変異率（デフォルト: 0.1） */
  mutationRate: number;
  /** 突然変異強度（デフォルト: 0.2） */
  mutationStrength: number;
  /** 交叉率（デフォルト: 0.7） */
  crossoverRate: number;
  /** トーナメントサイズ（デフォルト: 5） */
  tournamentSize: number;
  /** 1個体あたりの評価対戦数（デフォルト: 20） */
  gamesPerEvaluation: number;
  /** 初期個体群にデフォルトゲノムを含めるか */
  includeDefaultGenome: boolean;
  /** 対戦相手タイプ（デフォルト: 'evolved'） */
  opponentType: OpponentType;
}

/** デフォルト設定 */
export const DEFAULT_GA_CONFIG: GAConfig = {
  populationSize: 50,
  generations: 100,
  eliteCount: 5,
  mutationRate: 0.1,
  mutationStrength: 0.2,
  crossoverRate: 0.7,
  tournamentSize: 5,
  gamesPerEvaluation: 20,
  includeDefaultGenome: true,
  opponentType: 'evolved',
};

/** 進化ログエントリ */
export interface EvolutionLog {
  generation: number;
  bestFitness: number;
  avgFitness: number;
  worstFitness: number;
  diversity: number;
  elapsedTime: number;
}

/** トレーニング結果 */
export interface TrainingResult {
  bestGenome: AIGenome;
  bestFitness: number;
  generations: number;
  evolutionLog: EvolutionLog[];
  totalTime: number;
}

/** 進化コールバック */
export type EvolutionCallback = (
  generation: number,
  best: Individual,
  population: Individual[],
  log: EvolutionLog
) => void;

// =============================================================================
// 適応度評価
// =============================================================================

/** 対戦スコア計算パラメータ */
interface ScoreParams {
  /** 勝利ポイント */
  winPoints: number;
  /** 引き分けポイント */
  drawPoints: number;
  /** 敗北ポイント */
  losePoints: number;
  /** ライフ差ボーナス倍率 */
  lifeDiffBonus: number;
  /** 最大ライフ差ボーナス */
  maxLifeBonus: number;
}

const DEFAULT_SCORE_PARAMS: ScoreParams = {
  winPoints: 100,
  drawPoints: 30,
  losePoints: 0,
  lifeDiffBonus: 0.01,
  maxLifeBonus: 20,
};

/**
 * 対戦結果からスコアを計算
 */
function calculateScore(
  winner: PlayerNumber | 0 | null,
  myPlayer: PlayerNumber,
  myLife: number,
  opponentLife: number,
  params: ScoreParams = DEFAULT_SCORE_PARAMS
): number {
  let score = 0;

  // 勝敗スコア
  if (winner === myPlayer) {
    score = params.winPoints;
  } else if (winner === 0 || winner === null) {
    score = params.drawPoints;
  } else {
    score = params.losePoints;
  }

  // ライフ差ボーナス
  const lifeDiff = myLife - opponentLife;
  const lifeBonus = Math.min(
    lifeDiff * params.lifeDiffBonus,
    params.maxLifeBonus
  );
  score += lifeBonus;

  return score;
}

/**
 * シミュレーション用インポート（動的インポートで循環参照を回避）
 */
let simulateGameFn: ((config: {
  deck1: CardWithId[];
  deck2: CardWithId[];
  firstPlayer?: PlayerNumber;
  maxTurns?: number;
  p1Strategy?: unknown;
  p2Strategy?: unknown;
}) => {
  winner: PlayerNumber | 0 | null;
  p1Life: number;
  p2Life: number;
}) | null = null;

async function getSimulateGame() {
  if (!simulateGameFn) {
    // 動的インポートで循環参照を回避
    const module = await import('../../gameEngine/Simulator');
    simulateGameFn = module.simulateGame;
  }
  return simulateGameFn;
}

/**
 * ゲノムの適応度を評価
 *
 * 複数のデッキに対して対戦し、スコアを合計。
 */
export async function evaluateFitness(
  genome: AIGenome,
  trainingDecks: CardWithId[][],
  opponentDecks: CardWithId[][],
  gamesPerPair: number = 10
): Promise<number> {
  const simulateGame = await getSimulateGame();
  let totalScore = 0;

  // ゲノムからストラテジーを作成（シミュレーション評価含む全パラメータを使用）
  const genomeStrategy = createGeneticSimulationStrategy(genome);

  // 対戦相手: 現在の進化型AI（EVOLVED_GENOME）
  const opponentStrategy = createGeneticSimulationStrategy(EVOLVED_GENOME);

  for (const myDeck of trainingDecks) {
    for (const opponentDeck of opponentDecks) {
      for (let i = 0; i < gamesPerPair; i++) {
        // 先攻・後攻を交互に
        const firstPlayer: PlayerNumber = (i % 2 === 0 ? 1 : 2);

        const result = simulateGame({
          deck1: myDeck,
          deck2: opponentDeck,
          firstPlayer,
          p1Strategy: genomeStrategy,
          p2Strategy: opponentStrategy, // 現在の進化型AIと比較
        });

        // P1としての評価
        totalScore += calculateScore(
          result.winner,
          1,
          result.p1Life,
          result.p2Life
        );
      }
    }
  }

  return totalScore;
}

/**
 * 対戦相手ストラテジーを取得
 */
function getOpponentStrategy(opponentType: OpponentType, genome?: AIGenome): AIStrategy {
  switch (opponentType) {
    case 'hard':
      return hardStrategy;
    case 'self':
      // 自分自身（訓練中のゲノム）と対戦
      return genome ? createGeneticSimulationStrategy(genome) : createGeneticSimulationStrategy(EVOLVED_GENOME);
    case 'evolved':
    default:
      return createGeneticSimulationStrategy(EVOLVED_GENOME);
  }
}

/**
 * 同期版の適応度評価（既にシミュレータがロード済みの場合）
 */
export function evaluateFitnessSync(
  genome: AIGenome,
  trainingDecks: CardWithId[][],
  opponentDecks: CardWithId[][],
  gamesPerPair: number = 10,
  simulateGame: (config: {
    deck1: CardWithId[];
    deck2: CardWithId[];
    firstPlayer?: PlayerNumber;
    p1Strategy?: unknown;
    p2Strategy?: unknown;
  }) => {
    winner: PlayerNumber | 0 | null;
    p1Life: number;
    p2Life: number;
  },
  opponentType: OpponentType = 'evolved'
): number {
  let totalScore = 0;

  // ゲノムからストラテジーを作成（シミュレーション評価含む全パラメータを使用）
  const genomeStrategy = createGeneticSimulationStrategy(genome);

  // 対戦相手ストラテジーを取得
  const opponentStrategy = getOpponentStrategy(opponentType, genome);

  for (const myDeck of trainingDecks) {
    for (const opponentDeck of opponentDecks) {
      for (let i = 0; i < gamesPerPair; i++) {
        // 先攻・後攻を交互に
        const firstPlayer: PlayerNumber = (i % 2 === 0 ? 1 : 2);

        const result = simulateGame({
          deck1: myDeck,
          deck2: opponentDeck,
          firstPlayer,
          p1Strategy: genomeStrategy,
          p2Strategy: opponentStrategy,
        });

        // P1としての評価
        totalScore += calculateScore(
          result.winner,
          1,
          result.p1Life,
          result.p2Life
        );
      }
    }
  }

  return totalScore;
}

// =============================================================================
// 個体群管理
// =============================================================================

/**
 * 初期個体群を生成
 */
export function createInitialPopulation(
  size: number,
  includeDefault: boolean = true
): AIGenome[] {
  const population: AIGenome[] = [];

  // デフォルトゲノム（hardAI相当）を含める
  if (includeDefault) {
    population.push(cloneGenome(DEFAULT_GENOME));
  }

  // 残りをランダム生成
  while (population.length < size) {
    population.push(createRandomGenome());
  }

  return population;
}

/**
 * 個体群の多様性を計算
 *
 * 全ペア間の平均差分を返す。
 */
export function calculateDiversity(population: Individual[]): number {
  if (population.length < 2) return 0;

  let totalDiff = 0;
  let count = 0;

  for (let i = 0; i < population.length; i++) {
    for (let j = i + 1; j < population.length; j++) {
      // getGenomeDifferenceを使用
      const { getGenomeDifference } = require('./genome');
      totalDiff += getGenomeDifference(population[i].genome, population[j].genome);
      count++;
    }
  }

  return count > 0 ? totalDiff / count : 0;
}

// =============================================================================
// GeneticTrainer クラス
// =============================================================================

/**
 * 遺伝的アルゴリズムトレーナー
 */
export class GeneticTrainer {
  private config: GAConfig;
  private population: Individual[] = [];
  private evolutionLog: EvolutionLog[] = [];
  private startTime: number = 0;

  constructor(config: Partial<GAConfig> = {}) {
    this.config = { ...DEFAULT_GA_CONFIG, ...config };
  }

  /**
   * トレーニングを実行
   *
   * @param trainingDecks - 訓練用デッキ群
   * @param opponentDecks - 対戦相手デッキ群（省略時はtrainingDecksを使用）
   * @param callback - 世代ごとのコールバック
   * @returns トレーニング結果
   */
  async train(
    trainingDecks: CardWithId[][],
    opponentDecks?: CardWithId[][],
    callback?: EvolutionCallback
  ): Promise<TrainingResult> {
    this.startTime = Date.now();
    this.evolutionLog = [];

    // 対戦相手デッキのデフォルト
    const opponents = opponentDecks || trainingDecks;

    // シミュレータを事前ロード
    const simulateGame = await getSimulateGame();

    // 初期個体群を生成
    const genomes = createInitialPopulation(
      this.config.populationSize,
      this.config.includeDefaultGenome
    );

    // 適応度を評価
    this.population = await this.evaluatePopulation(
      genomes,
      trainingDecks,
      opponents,
      simulateGame
    );

    // 進化ループ
    for (let gen = 0; gen < this.config.generations; gen++) {
      // 次世代を生成
      this.population = this.evolveGeneration(this.population);

      // 適応度を評価
      this.population = await this.evaluatePopulation(
        this.population.map(i => i.genome),
        trainingDecks,
        opponents,
        simulateGame
      );

      // ログ記録
      const log = this.createEvolutionLog(gen);
      this.evolutionLog.push(log);

      // コールバック
      if (callback) {
        const best = this.getBestIndividual();
        callback(gen, best, this.population, log);
      }
    }

    // 最良個体を返す
    const best = this.getBestIndividual();
    return {
      bestGenome: best.genome,
      bestFitness: best.fitness,
      generations: this.config.generations,
      evolutionLog: this.evolutionLog,
      totalTime: Date.now() - this.startTime,
    };
  }

  /**
   * カスタム初期個体群でトレーニングを実行
   *
   * Stage 2訓練など、特定のパラメータだけを最適化する場合に使用。
   * 閾値パラメータを固定し、シミュレーション評価パラメータのみを進化させる等。
   *
   * @param initialPopulation - 初期個体群（ゲノム配列）
   * @param trainingDecks - 訓練用デッキ群
   * @param opponentDecks - 対戦相手デッキ群
   * @param callback - 世代ごとのコールバック
   * @returns トレーニング結果
   */
  async trainWithInitialPopulation(
    initialPopulation: AIGenome[],
    trainingDecks: CardWithId[][],
    opponentDecks: CardWithId[][],
    callback?: EvolutionCallback
  ): Promise<TrainingResult> {
    this.startTime = Date.now();
    this.evolutionLog = [];

    // シミュレータを事前ロード
    const simulateGame = await getSimulateGame();

    // 提供された初期個体群を使用（サイズを調整）
    let genomes = [...initialPopulation];

    // 個体数が足りない場合はランダムで補充
    while (genomes.length < this.config.populationSize) {
      genomes.push(createRandomGenome());
    }

    // 個体数が多すぎる場合は切り詰め
    if (genomes.length > this.config.populationSize) {
      genomes = genomes.slice(0, this.config.populationSize);
    }

    // 適応度を評価
    this.population = await this.evaluatePopulation(
      genomes,
      trainingDecks,
      opponentDecks,
      simulateGame
    );

    // 進化ループ
    for (let gen = 0; gen < this.config.generations; gen++) {
      // 次世代を生成
      this.population = this.evolveGeneration(this.population);

      // 適応度を評価
      this.population = await this.evaluatePopulation(
        this.population.map(i => i.genome),
        trainingDecks,
        opponentDecks,
        simulateGame
      );

      // ログ記録
      const log = this.createEvolutionLog(gen);
      this.evolutionLog.push(log);

      // コールバック
      if (callback) {
        const best = this.getBestIndividual();
        callback(gen, best, this.population, log);
      }
    }

    // 最良個体を返す
    const best = this.getBestIndividual();
    return {
      bestGenome: best.genome,
      bestFitness: best.fitness,
      generations: this.config.generations,
      evolutionLog: this.evolutionLog,
      totalTime: Date.now() - this.startTime,
    };
  }

  /**
   * 同期版トレーニング（テスト用）
   */
  trainSync(
    trainingDecks: CardWithId[][],
    opponentDecks: CardWithId[][],
    simulateGame: (config: {
      deck1: CardWithId[];
      deck2: CardWithId[];
      firstPlayer?: PlayerNumber;
      p1Strategy?: unknown;
      p2Strategy?: unknown;
    }) => {
      winner: PlayerNumber | 0 | null;
      p1Life: number;
      p2Life: number;
    },
    callback?: EvolutionCallback
  ): TrainingResult {
    this.startTime = Date.now();
    this.evolutionLog = [];

    // 初期個体群を生成
    const genomes = createInitialPopulation(
      this.config.populationSize,
      this.config.includeDefaultGenome
    );

    // 適応度を評価
    this.population = this.evaluatePopulationSync(
      genomes,
      trainingDecks,
      opponentDecks,
      simulateGame
    );

    // 進化ループ
    for (let gen = 0; gen < this.config.generations; gen++) {
      // 次世代を生成
      this.population = this.evolveGeneration(this.population);

      // 適応度を評価
      this.population = this.evaluatePopulationSync(
        this.population.map(i => i.genome),
        trainingDecks,
        opponentDecks,
        simulateGame
      );

      // ログ記録
      const log = this.createEvolutionLog(gen);
      this.evolutionLog.push(log);

      // コールバック
      if (callback) {
        const best = this.getBestIndividual();
        callback(gen, best, this.population, log);
      }
    }

    // 最良個体を返す
    const best = this.getBestIndividual();
    return {
      bestGenome: best.genome,
      bestFitness: best.fitness,
      generations: this.config.generations,
      evolutionLog: this.evolutionLog,
      totalTime: Date.now() - this.startTime,
    };
  }

  /**
   * 個体群の適応度を評価
   */
  private async evaluatePopulation(
    genomes: AIGenome[],
    trainingDecks: CardWithId[][],
    opponentDecks: CardWithId[][],
    simulateGame: (config: {
      deck1: CardWithId[];
      deck2: CardWithId[];
      firstPlayer?: PlayerNumber;
      p1Strategy?: unknown;
      p2Strategy?: unknown;
    }) => {
      winner: PlayerNumber | 0 | null;
      p1Life: number;
      p2Life: number;
    }
  ): Promise<Individual[]> {
    const gamesPerPair = Math.ceil(
      this.config.gamesPerEvaluation / (trainingDecks.length * opponentDecks.length)
    );

    return genomes.map(genome => ({
      genome,
      fitness: evaluateFitnessSync(
        genome,
        trainingDecks,
        opponentDecks,
        gamesPerPair,
        simulateGame,
        this.config.opponentType
      ),
    }));
  }

  /**
   * 個体群の適応度を評価（同期版）
   */
  private evaluatePopulationSync(
    genomes: AIGenome[],
    trainingDecks: CardWithId[][],
    opponentDecks: CardWithId[][],
    simulateGame: (config: {
      deck1: CardWithId[];
      deck2: CardWithId[];
      firstPlayer?: PlayerNumber;
      p1Strategy?: unknown;
      p2Strategy?: unknown;
    }) => {
      winner: PlayerNumber | 0 | null;
      p1Life: number;
      p2Life: number;
    }
  ): Individual[] {
    const gamesPerPair = Math.ceil(
      this.config.gamesPerEvaluation / (trainingDecks.length * opponentDecks.length)
    );

    return genomes.map(genome => ({
      genome,
      fitness: evaluateFitnessSync(
        genome,
        trainingDecks,
        opponentDecks,
        gamesPerPair,
        simulateGame,
        this.config.opponentType
      ),
    }));
  }

  /**
   * 次世代を生成
   */
  private evolveGeneration(population: Individual[]): Individual[] {
    const nextGen: Individual[] = [];

    // エリート保存
    const elites = eliteSelection(population, this.config.eliteCount);
    for (const elite of elites) {
      nextGen.push({ genome: cloneGenome(elite.genome), fitness: 0 });
    }

    // 交叉と突然変異で残りを生成
    while (nextGen.length < this.config.populationSize) {
      // 親選択
      const parent1 = tournamentSelection(population, this.config.tournamentSize);
      const parent2 = tournamentSelection(population, this.config.tournamentSize);

      let child: AIGenome;

      // 交叉
      if (Math.random() < this.config.crossoverRate) {
        child = crossover(parent1.genome, parent2.genome);
      } else {
        child = cloneGenome(parent1.genome);
      }

      // 突然変異
      child = mutate(child, this.config.mutationRate, this.config.mutationStrength);

      // 正規化
      child = normalizeGenome(child);

      nextGen.push({ genome: child, fitness: 0 });
    }

    return nextGen;
  }

  /**
   * 進化ログを作成
   */
  private createEvolutionLog(generation: number): EvolutionLog {
    const fitnesses = this.population.map(i => i.fitness);
    const sum = fitnesses.reduce((a, b) => a + b, 0);

    return {
      generation,
      bestFitness: Math.max(...fitnesses),
      avgFitness: sum / fitnesses.length,
      worstFitness: Math.min(...fitnesses),
      diversity: calculateDiversity(this.population),
      elapsedTime: Date.now() - this.startTime,
    };
  }

  /**
   * 最良個体を取得
   */
  private getBestIndividual(): Individual {
    return this.population.reduce((best, current) =>
      current.fitness > best.fitness ? current : best
    );
  }

  /**
   * 現在の個体群を取得
   */
  getPopulation(): Individual[] {
    return this.population;
  }

  /**
   * 進化ログを取得
   */
  getEvolutionLog(): EvolutionLog[] {
    return this.evolutionLog;
  }
}

// =============================================================================
// ユーティリティ
// =============================================================================

/**
 * 簡易トレーニング関数
 *
 * デフォルト設定でトレーニングを実行。
 */
export async function trainAI(
  trainingDecks: CardWithId[][],
  generations: number = 50,
  callback?: EvolutionCallback
): Promise<TrainingResult> {
  const trainer = new GeneticTrainer({
    generations,
    populationSize: 30,
    gamesPerEvaluation: 10,
  });

  return trainer.train(trainingDecks, undefined, callback);
}
